import { type Ref, ref, watchEffect } from 'vue'

import { normalizeAdvertiser } from '@/lib/adapters/advertiser.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import type { AdvertiserLegacyType } from '@/lib/types/advertiser.types'
import { useErrorStore } from '@/stores/error'

export const useGetAdvertiserRequest = (idRef: Ref<number>) => {
  const isLoading = ref(false)
  const isFetching = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<AdvertiserLegacyType | null>(null)
  const errorStore = useErrorStore()

  const getAdvertiser = async (isReload = false) => {
    try {
      if (!isReload) {
        isFetching.value = true
      }
      isLoading.value = true
      error.value = null
      const id = idRef.value
      // Note: this is legacy API
      const response = await request.get(`/stores/${id}`)
      data.value = normalizeAdvertiser(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
      isFetching.value = false
    }
  }

  watchEffect(() => void getAdvertiser())

  return {
    data,
    getAdvertiser,
    isLoading,
    isFetching,
    error,
  }
}

export const useDeleteAdvertiserRequest = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  const errorStore = useErrorStore()

  const deleteAdvertiser = async (id: number) => {
    try {
      isLoading.value = true
      error.value = null
      await request.delete(`/stores/${id}`)
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    deleteAdvertiser,
    isLoading,
    error,
  }
}

export const useDuplicateAdvertiser = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const duplicateAdvertiser = async (id: number) => {
    try {
      isLoading.value = true
      error.value = null
      const { data } = await request.post(`/admin/stores/${id}/duplicate`, {})
      return data
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    duplicateAdvertiser,
    isLoading,
    error,
  }
}
